import { useEffect } from 'react'

import { PointArray } from '@editor/interfaces/attribute'
import {
  Drawing,
  DrawingParameterMeasurementLabel,
  DrawingParameterTextAnnotation,
  DrawingType,
} from '@editor/interfaces/drawing'
import { isDrawingOfType } from '@editor/services/Drawing'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { useThree } from '@react-three/fiber'
import { inRange } from 'lodash'
import { MathUtils, Matrix4, Quaternion, Vector3 } from 'three'

import { useBlueprintEditorContext } from '../contexts/BlueprintEditorContext'
import useProjectionPlane from '../hooks/useProjectionPlane'
import { ExportState, exportDrawings, set2dDrawings } from '../store/drawings'

/**
 * Manages the 2D drawings that correspond to the 3D drawings in the main document.
 * The 2D drawings later will be used for image export and PDF generation.
 */
const Drawings2DManager = () => {
  // Hooks
  const { camera, gl } = useThree()
  const { worldToScreen, projectionDirections } = useProjectionPlane(camera, gl)
  const { orthographicCamera } = useBlueprintEditorContext()

  // Store
  const dispatch = useAppDispatch()
  const doc = useAppSelector((state) => state.blueprint.drawings.document)
  const document2d = useAppSelector((state) => state.blueprint.drawings.document2d)

  /**
   * On update of document (drawings), update the 2D document as well.
   */
  useEffect(() => {
    if (!projectionDirections || document2d.exportState !== ExportState.ProcessData) return

    // Calculate mm to px ratio for measurement label conversion
    let mmToPxRatio = 1
    const referenceLengthMm = 1 / 1000 // in mm
    const refPt1 = new Vector3(0, 0, 0)
    const refPt2 = new Vector3(...projectionDirections.up).multiplyScalar(referenceLengthMm)
    const refPt1v2 = worldToScreen(refPt1)
    const refPt2v2 = worldToScreen(refPt2)
    if (refPt1v2 && refPt2v2) {
      mmToPxRatio = refPt1v2.distanceTo(refPt2v2)
    }

    const canvas3dRect = gl.domElement.getBoundingClientRect()
    const texts = doc.drawings.reduce<Drawing[]>((acc, drawing) => {
      if (isDrawingOfType<DrawingParameterMeasurementLabel>(drawing, DrawingType.MeasurementLabel)) {
        const pt1v3 = new Vector3().fromArray(drawing.drawing_parameters.points[0])
        const pt2v3 = new Vector3().fromArray(drawing.drawing_parameters.points[1])
        const pt1v2 = worldToScreen(pt1v3)
        const pt2v2 = worldToScreen(pt2v3)
        if (!pt1v2 || !pt2v2) return acc

        const pos2zero = pt2v2.clone().sub(pt1v2)
        const angle = MathUtils.radToDeg(pos2zero.angle())

        acc.push({
          ...drawing,
          drawing_parameters: {
            ...drawing.drawing_parameters,
            // prettier-ignore
            points: [
              // screen position x,y of the first point and rotation angle
              (inRange(angle, 90, 270) ? [pt2v2.x, pt2v2.y, angle-180] : [pt1v2.x, pt1v2.y, angle]) as PointArray,

              // screen-space width of pt1->pt2, actual distance (in mm), 0 or 1 based on angle
              [pt1v2.distanceTo(pt2v2), pt1v3.distanceTo(pt2v3), inRange(angle, 90, 270) ? 1 : 0] as PointArray,
            ],
          },
        })
      } else if (isDrawingOfType<DrawingParameterTextAnnotation>(drawing, DrawingType.TextAnnotation)) {
        const matrix = new Matrix4().fromArray(drawing.drawing_parameters.transformation)
        const pt1v3 = new Vector3()
        matrix.decompose(pt1v3, new Quaternion(), new Vector3())

        const pt1v2 = worldToScreen(pt1v3)
        if (!pt1v2) return acc

        const leadLeft = document.querySelector(`[data-text-annotation-lead-left='${drawing.drawing_parameters.uuid}']`)
        if (!leadLeft) return acc

        const leadLeftRect = leadLeft.getBoundingClientRect()

        matrix.setPosition(
          leadLeftRect.x - canvas3dRect.x,
          leadLeftRect.y + leadLeftRect.height / 2 - canvas3dRect.y,
          0,
        )

        acc.push({
          ...drawing,
          drawing_parameters: {
            ...drawing.drawing_parameters,
            transformation: matrix.toArray(),
          },
        })
      }

      return acc
    }, [])

    dispatch(set2dDrawings({ drawings: texts, mmToPxRatio, scale: orthographicCamera.current.zoom }))
    setTimeout(() => {
      dispatch(exportDrawings(ExportState.Exporting))
    }, 1000)
  }, [doc, document2d, gl.domElement, projectionDirections, orthographicCamera, worldToScreen, dispatch])

  return null
}

export default Drawings2DManager
